import { useEffect, useRef, useState, useCallback } from 'react';
import { readDraft, writeDraft, clearDraft } from './draftStore';

/**
 * Универсальный автосейв-хук для wizard-форм.
 *
 * Что делает:
 *  • При монтировании: читает черновик из localStorage по `key`, если есть
 *    — отдаёт через onRestore (с timestamp когда был сохранён). Юзер сам
 *    решает применить ли (через restoredAt + кнопку "восстановить").
 *  • На каждое изменение value: debounced write в localStorage.
 *  • Защита от закрытия вкладки: если есть несохранённые изменения
 *    (savedAt < lastEditAt), показывает beforeunload-warning.
 *  • После успешной отправки: caller вызывает clear() → черновик стирается.
 *
 * @param {string} key - уникальный ключ для localStorage (напр. 'quiz_create')
 * @param {any} value - текущее значение формы (object/array/string)
 * @param {object} options
 * @param {number} [options.debounceMs=500] - задержка перед сохранением
 * @param {boolean} [options.warnOnUnload=true] - предупреждать перед закрытием
 * @param {(value: any) => boolean} [options.shouldSave] - фильтр, нужно ли сохранять
 *   (например, не сохранять пустую форму). Дефолт: всегда сохраняем.
 * @returns {{
 *   restored: { value: any, savedAt: number } | null,
 *   restore: () => void,
 *   dismissRestore: () => void,
 *   clear: () => void,
 *   savedAt: number | null,
 * }}
 */
export function useDraftAutosave(key, value, options = {}) {
  const {
    debounceMs = 500,
    warnOnUnload = true,
    shouldSave = () => true,
  } = options;

  // Восстановленный черновик (если был при mount). null = нечего восстанавливать.
  const [restored, setRestored] = useState(null);
  // Timestamp последнего успешного сохранения.
  const [savedAt, setSavedAt] = useState(null);
  // Флаг "восстановлен"/"отклонён" чтобы не показывать UI после того как юзер решил.
  const restoredHandledRef = useRef(false);

  // ─── Mount: пытаемся восстановить ────────────────────────────────
  useEffect(() => {
    const draft = readDraft(key);
    if (draft && typeof draft === 'object' && 'value' in draft && 'savedAt' in draft) {
      setRestored({ value: draft.value, savedAt: draft.savedAt });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // ─── Debounced save ──────────────────────────────────────────────
  // Не пишем пока юзер не решил с восстановлением (иначе сразу затрём draft
  // черновика тем что в state, до того как юзер успеет нажать "восстановить").
  useEffect(() => {
    if (!restoredHandledRef.current && restored) return;
    if (!shouldSave(value)) return;

    const t = setTimeout(() => {
      writeDraft(key, { value, savedAt: Date.now() });
      setSavedAt(Date.now());
    }, debounceMs);

    return () => clearTimeout(t);
  }, [key, value, debounceMs, restored, shouldSave]);

  // ─── beforeunload warning ────────────────────────────────────────
  useEffect(() => {
    if (!warnOnUnload) return undefined;
    const handler = (e) => {
      if (!shouldSave(value)) return;
      // Браузер сам показывает стандартный prompt. Кастомный текст
      // современные браузеры игнорируют.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [value, warnOnUnload, shouldSave]);

  // ─── API для caller'а ────────────────────────────────────────────
  const restore = useCallback(() => {
    restoredHandledRef.current = true;
    setRestored(null);
  }, []);

  const dismissRestore = useCallback(() => {
    restoredHandledRef.current = true;
    setRestored(null);
    clearDraft(key);
  }, [key]);

  const clear = useCallback(() => {
    clearDraft(key);
    setSavedAt(null);
    restoredHandledRef.current = true;
  }, [key]);

  return { restored, restore, dismissRestore, clear, savedAt };
}

/**
 * Хелпер: отформатировать "сохранено N минут назад".
 */
export function formatAgo(timestamp) {
  if (!timestamp) return '';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 5) return 'только что';
  if (seconds < 60) return `${seconds} сек. назад`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин. назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч. назад`;
  return `${Math.floor(hours / 24)} дн. назад`;
}
